import React from "react";
import { Link } from "react-router-dom";
import { Heart, Share2, MapPin, BedDouble, Bath, Maximize, Car, BadgeCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

export default function PropertyCard({ property, onLoginRequired }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const purposeLabel = { sale: t("common.forSale"), rent: t("common.forRent"), lease: t("common.forLease") }[property.purpose] || t("common.forSale");
  const isPending = property.status === "pending" || !property.verified;

  const toggleFav = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { onLoginRequired?.(); return; }
    try {
      const r = await api.post(`/favorites/${property.id}`);
      toast.success(r.data.favorited ? "Saved to favorites" : "Removed from favorites");
    } catch { toast.error("Could not update favorites"); }
  };

  const share = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const url = `${window.location.origin}/property/${property.id}`;
    if (navigator.share) { try { await navigator.share({ title: property.title, url }); } catch {} }
    else { try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch {} }
  };

  return (
    <Link to={`/property/${property.id}`} className="block group" data-testid={`property-card-${property.id}`}>
      <article className="bg-white border border-slate-200 rounded-xl overflow-hidden card-hover">
        <div className="relative">
          <img loading="lazy" src={property.images?.[0]} alt={property.title} className="w-full h-52 object-cover"/>
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            <span className="bg-kp-maroon text-white text-[11px] font-semibold px-2.5 py-1 rounded-full">{purposeLabel}</span>
            {property.verified ? (
              <span className="bg-kp-teal text-white text-[11px] font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
                <BadgeCheck size={12}/> {t("common.verified")}
              </span>
            ) : isPending ? (
              <span className="bg-amber-500 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
                <Clock size={12}/> {t("common.pending")}
              </span>
            ) : null}
          </div>
          <div className="absolute top-3 right-3 flex gap-2">
            <button onClick={toggleFav} className="w-9 h-9 rounded-full bg-white/95 hover:bg-kp-maroon hover:text-white flex items-center justify-center transition-colors shadow-md" data-testid={`fav-btn-${property.id}`}><Heart size={16}/></button>
            <button onClick={share} className="w-9 h-9 rounded-full bg-white/95 hover:bg-kp-teal hover:text-white flex items-center justify-center transition-colors shadow-md" data-testid={`share-btn-${property.id}`}><Share2 size={16}/></button>
          </div>
        </div>
        <div className="p-4">
          <h3 className="font-display font-semibold text-slate-900 text-lg leading-snug group-hover:text-kp-teal transition-colors line-clamp-1">{property.title}</h3>
          <p className="flex items-center gap-1 text-sm text-slate-500 mt-1"><MapPin size={13}/> {property.locality}, {property.city}</p>
          <div className="mt-3 flex items-baseline justify-between">
            <span className="font-display text-xl font-bold text-kp-maroon">{property.price_label}</span>
            <span className="text-xs text-slate-500">{property.property_type}</span>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-600">
            {property.bedrooms != null && <span className="flex items-center gap-1"><BedDouble size={13}/> {property.bedrooms} {t("common.beds")}</span>}
            {property.bathrooms != null && <span className="flex items-center gap-1"><Bath size={13}/> {property.bathrooms} {t("common.baths")}</span>}
            <span className="flex items-center gap-1"><Maximize size={13}/> {property.area} {t("common.sqft")}</span>
            {property.parking != null && <span className="flex items-center gap-1"><Car size={13}/> {property.parking}</span>}
          </div>
        </div>
      </article>
    </Link>
  );
}
